import _ from "lodash";
import React, { useMemo } from "react";
import classes from "./GroupedProjectGroupList.scss";
import { useI18n } from "Hooks";
import ProjectGroupCard from "./components/ProjectGroupCard/ProjectGroupCard";
import ProjectGroupCardV2 from "./components/ProjectGroupCard/ProjectGroupCardV2";
import { connect } from "react-redux";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { PROJECT_TYPE_LABEL_MAP_V2 } from "Projects/modules/ProjectModules";
import { GLOBAL_HEADER_SHORTCUTS_MAPPING } from "Platform/modules/PlatformUtils";
import {
  getUserDataBasedOnUserType,
  getFilteredProjectGroupTypes,
  getProjectGroupCategoryIdByType,
  getUniqueProjectGroupTypes,
} from "Projects/modules/Utils";
import {
  PROJECT_GROUP_TYPE_DP_CAS,
  PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
  PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
  PROJECT_GROUP_TYPE_DP_EE,
  PROJECT_GROUP_TYPE_DP_G4_PROJECT,
  PROJECT_GROUP_TYPE_PERSONAL,
  PROJECT_GROUP_TYPE_COMMUNITY,
  PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
  PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT,
  PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT,
  PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING,
  PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT,
} from "Projects/Constants/stringConstants";
import ShortcutTooltip from "UIComponents/ShortcutTooltip";
import { goToRelativeRoute } from "modules/Services";
import { colors } from "Constants";
import {
  CommunityProject3D,
  PersonalProject3D,
  Cas3D,
  ServiceAsAction3D,
  Document3D,
  BookAtomBlue3D,
  BookAtomViolet3D,
  ServiceLearning3D,
  ReflectiveProject3D,
  CommunityEngagement3D,
} from "@toddle-edu/ds-icons";
import { useKeyboardShortcutsContext } from "@toddle-edu/ds-web";
import classNames from "classnames";

export const stylesV2 = {
  defaultStyle: { backgroundColor: "var(--green-900)" },
  [PROJECT_GROUP_TYPE_DP_CAS]: {
    backgroundColor: "var(--yellow-900)",
  },
  [PROJECT_GROUP_TYPE_DP_TOK_ESSAY]: {
    backgroundColor: "var(--violet-900)",
  },
  [PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION]: {
    backgroundColor: "var(--orange-900)",
  },
  [PROJECT_GROUP_TYPE_DP_EE]: { backgroundColor: "var(--pink-900)" },
  [PROJECT_GROUP_TYPE_DP_G4_PROJECT]: { backgroundColor: "var(--violet-900)" },
  [PROJECT_GROUP_TYPE_PERSONAL]: { backgroundColor: "var(--pink-900)" },
  [PROJECT_GROUP_TYPE_COMMUNITY]: { backgroundColor: "var(--orange-900)" },
  [PROJECT_GROUP_TYPE_SERVICE_AS_ACTON]: { backgroundColor: "var(--blue-900)" },
  [PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT]: {
    backgroundColor: "var(--blue-900)",
  },
  [PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT]: {
    backgroundColor: "var(--violet-900)",
  },
  [PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING]: {
    backgroundColor: "var(--orange-900)",
  },
  [PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT]: {
    backgroundColor: "var(--violet-900)",
  },
};

const stylesV1 = {
  defaultStyle: { borderLeftColor: colors.blue400 },
  [PROJECT_GROUP_TYPE_DP_CAS]: {
    borderLeftColor: colors.interactiveThreeDefault,
  },
  [PROJECT_GROUP_TYPE_DP_TOK_ESSAY]: {
    borderLeftColor: colors.borderLink,
  },
  [PROJECT_GROUP_TYPE_DP_G4_PROJECT]: {
    borderLeftColor: colors.borderLink,
  },
  [PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION]: {
    borderLeftColor: colors.borderLink,
  },
  [PROJECT_GROUP_TYPE_DP_EE]: { borderLeftColor: colors.interactiveOneDefault },
  [PROJECT_GROUP_TYPE_PERSONAL]: {
    borderLeftColor: colors.interactiveOneDefault,
  },
  [PROJECT_GROUP_TYPE_COMMUNITY]: { borderLeftColor: colors.borderLink },
  [PROJECT_GROUP_TYPE_SERVICE_AS_ACTON]: {
    borderLeftColor: colors.interactiveThreeDefault,
  },
};

export const getIconComponent = ({ projectGroupType, iconProps = {} }) => {
  switch (projectGroupType) {
    case PROJECT_GROUP_TYPE_DP_CAS:
      return <Cas3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING:
      return <ServiceLearning3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT:
      return <CommunityEngagement3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_SERVICE_AS_ACTON:
      return <ServiceAsAction3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_DP_TOK_ESSAY:
      return <Document3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_DP_G4_PROJECT:
      return <BookAtomViolet3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION:
    case PROJECT_GROUP_TYPE_COMMUNITY:
      return <CommunityProject3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT:
      return <ReflectiveProject3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_DP_EE:
    case PROJECT_GROUP_TYPE_PERSONAL:
      return <PersonalProject3D {...iconProps} />;
    case PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT:
      return <BookAtomBlue3D {...iconProps} />;
  }
};
const projectGroupOrder = {
  [PROJECT_GROUP_TYPE_DP_CAS]: 1,
  [PROJECT_GROUP_TYPE_DP_EE]: 2,
  [PROJECT_GROUP_TYPE_DP_TOK_ESSAY]: 3,
  [PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION]: 4,
  [PROJECT_GROUP_TYPE_PERSONAL]: 5,
  [PROJECT_GROUP_TYPE_COMMUNITY]: 6,
  [PROJECT_GROUP_TYPE_SERVICE_AS_ACTON]: 7,
};

const GroupedProjectList = props => {
  const {
    projectGroups,
    goToRelativeRoute,
    currentCurriculumProgramType,
    shouldRenderIconCards = false,
    showThreeItemsInRow = true,
    label,
    customOnCardClick,
    showCardsAsColumnFlexed = false,
    userId,
    currentCurriculumProgramId,
    organizationId,
    isUserTypeParent,
    userType,
    isGlobalHeaderPresent,
    groupedContainerCustomClass = "",
  } = props;

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  const { t } = useI18n(["project", "common"]);

  const { areShortcutsEnabled } = useKeyboardShortcutsContext();

  const isRevampUI = shouldRenderIconCards;

  const projectGroupTypes = useMemo(
    () =>
      getUniqueProjectGroupTypes({
        projectGroups,
      }),
    [projectGroups]
  );
  const onCardClick = projectGroupType => {
    goToRelativeRoute({
      route: `../project-group-types/${projectGroupType}`,
    });
  };

  const onProjectCardClick = projectGroupType => {
    if (customOnCardClick) {
      customOnCardClick(projectGroupType);
    } else onCardClick(projectGroupType);
  };

  const groupedContainer = classNames({
    [classes.groupedContainer]: true,
    [classes.threeGroupedContainer]:
      showThreeItemsInRow && !isGlobalHeaderPresent,
    [classes.fourGroupedContainer]:
      userType === "STAFF" && isGlobalHeaderPresent,
    [classes.flexedGroupedContainer]: showCardsAsColumnFlexed,
    [groupedContainerCustomClass]: true,
  });

  const labelClasses = classNames({
    [classes.labelText]: !isGlobalHeaderPresent,
    [classes.labelTextV2]: isGlobalHeaderPresent,
  });

  const filteredProjectGroupTypes = getFilteredProjectGroupTypes({
    projectGroupTypes,
    projectGroupCategoryIdByType,
    isUserTypeParent,
    organizationId,
    userId,
    currentCurriculumProgramId,
  });

  return _.size(filteredProjectGroupTypes) > 0 ? (
    <div className={classes.container}>
      <h6 className={labelClasses}>{label}</h6>
      <div className={groupedContainer}>
        {_.map(filteredProjectGroupTypes, (projectGroupType, index) => {
          const projectGroupShortcut =
            GLOBAL_HEADER_SHORTCUTS_MAPPING["PROJECT_" + (index + 1)];

          if (isRevampUI) {
            return (
              <ShortcutTooltip
                showTooltip={
                  !!(
                    !_.isEmpty(projectGroupShortcut) &&
                    isGlobalHeaderPresent &&
                    areShortcutsEnabled
                  )
                }
                label={t(PROJECT_TYPE_LABEL_MAP_V2[projectGroupType])}
                shortcutKeys={[
                  ["J"],
                  [
                    ...(projectGroupShortcut?.shiftKey ? ["Shift"] : []),
                    projectGroupShortcut?.shortcutKey,
                  ],
                ]}
                key={projectGroupType}
              >
                <div>
                  <ProjectGroupCardV2
                    isGlobalHeaderPresent={isGlobalHeaderPresent}
                    iconStyle={
                      stylesV2[projectGroupType] || stylesV2.defaultStyle
                    }
                    label={t(PROJECT_TYPE_LABEL_MAP_V2[projectGroupType])}
                    onCardClick={() => {
                      onProjectCardClick(projectGroupType);
                    }}
                    icon={getIconComponent({ projectGroupType })}
                    showCardsAsColumnFlexed={showCardsAsColumnFlexed}
                    testId={`button-dashboard-projectGroup-${projectGroupType}`}
                  />
                </div>
              </ShortcutTooltip>
            );
          } else {
            return (
              <ProjectGroupCard
                key={projectGroupType}
                containerStyle={
                  stylesV1[projectGroupType] || stylesV1.defaultStyle
                }
                label={t(PROJECT_TYPE_LABEL_MAP_V2[projectGroupType])}
                onCardClick={() => {
                  onProjectCardClick(projectGroupType);
                }}
              />
            );
          }
        })}
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state, ownProps) => {
  // in case of visitors we need to get the curriculumId from the parent component
  const curriculumId = _.get(ownProps, "curriculumId", null);
  const userInfo = getUserInfo();
  const userData = getUserDataBasedOnUserType();
  const userType = _.get(userData, "userType", "");
  const currentCurriculumProgramId =
    _.get(state, "platform.currentCurriculumProgram.id") || curriculumId;
  return {
    userType,
    currentCurriculumProgramId,
    userId: userInfo.id,
    organizationId: userInfo.org_id,
    isUserTypeParent: userData.isUserTypeParent,
  };
};

const mapActionCreators = {
  goToRelativeRoute,
};

export default connect(mapStateToProps, mapActionCreators)(GroupedProjectList);
